/* ==========================================================================
   Future Aesthetics — wishlist
   Shopify has no native wishlist, so favourites live in localStorage on the
   shopper's device. Three pieces talk to one store:
     <fa-wishlist-button>  toggles a product
     <fa-wishlist-count>   renders the header bubble
     <fa-wishlist-list>    renders the saved items on the wishlist page
   ========================================================================== */

(function () {
  'use strict';

  const STORAGE_KEY = 'fa:wishlist';
  const CHANGE_EVENT = 'fa:wishlist:change';

  const Wishlist = {
    read() {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    },

    write(items) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (error) {
        /* Private browsing or a full quota — the UI still updates for this page view. */
      }
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
    },

    has(id) {
      return this.read().some((item) => String(item.id) === String(id));
    },

    toggle(product) {
      const items = this.read();
      const index = items.findIndex((item) => String(item.id) === String(product.id));

      if (index > -1) {
        items.splice(index, 1);
      } else {
        items.unshift(product);
      }

      this.write(items);
      return index === -1;
    },

    remove(id) {
      this.write(this.read().filter((item) => String(item.id) !== String(id)));
    },

    count() {
      return this.read().length;
    },
  };

  window.FaWishlist = Wishlist;

  /* ---------------------------------------------------------------------- */

  class FaWishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.sync();
      this.button.addEventListener('click', this.onClick.bind(this));
      this.syncHandler = this.sync.bind(this);
      document.addEventListener(CHANGE_EVENT, this.syncHandler);
    }

    disconnectedCallback() {
      if (this.syncHandler) document.removeEventListener(CHANGE_EVENT, this.syncHandler);
    }

    get product() {
      const data = this.dataset;
      return {
        id: data.productId,
        handle: data.productHandle,
        title: data.productTitle,
        url: data.productUrl,
        price: data.productPrice,
        image: data.productImage,
      };
    }

    onClick(event) {
      event.preventDefault();
      const added = Wishlist.toggle(this.product);
      this.button.setAttribute('aria-pressed', String(added));
    }

    sync() {
      this.button.setAttribute('aria-pressed', String(Wishlist.has(this.dataset.productId)));
    }
  }

  class FaWishlistCount extends HTMLElement {
    connectedCallback() {
      this.render();
      this.renderHandler = this.render.bind(this);
      document.addEventListener(CHANGE_EVENT, this.renderHandler);
      window.addEventListener('storage', this.renderHandler);
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.renderHandler);
      window.removeEventListener('storage', this.renderHandler);
    }

    render() {
      const count = Wishlist.count();
      this.textContent = count > 99 ? '99+' : String(count);
      this.hidden = count === 0;

      const link = this.closest('.fa-header__icon--wishlist');
      if (link) link.classList.toggle('is-active', count > 0);
    }
  }

  class FaWishlistList extends HTMLElement {
    connectedCallback() {
      this.render();
      this.renderHandler = this.render.bind(this);
      document.addEventListener(CHANGE_EVENT, this.renderHandler);

      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-wishlist-remove]');
        if (!remove) return;
        event.preventDefault();
        Wishlist.remove(remove.dataset.wishlistRemove);
      });
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.renderHandler);
    }

    render() {
      const items = Wishlist.read();

      if (!items.length) {
        this.innerHTML = `
          <div class="fa-wishlist__empty">
            <p class="fa-body">${this.dataset.emptyText || 'Your wishlist is empty.'}</p>
            <a class="fa-btn fa-btn--solid" href="${this.dataset.emptyUrl || '/collections/all'}">
              ${this.dataset.emptyCta || 'Start shopping'}
            </a>
          </div>`;
        return;
      }

      const cards = items
        .map(
          (item) => `
          <li class="fa-card">
            <div class="fa-card__media-wrap">
              <a href="${escapeAttr(item.url)}" class="fa-card__media fa-media fa-media--portrait">
                ${
                  item.image
                    ? `<img class="fa-card__image fa-card__image--primary" src="${escapeAttr(
                        item.image
                      )}" alt="${escapeAttr(item.title)}" loading="lazy">`
                    : ''
                }
              </a>
              <button
                type="button"
                class="fa-card__wishlist fa-card__wishlist--mark"
                aria-pressed="true"
                data-wishlist-remove="${escapeAttr(item.id)}"
                aria-label="Remove ${escapeAttr(item.title)} from wishlist"
              ><span class="fa-card__wishlist-mark" aria-hidden="true"></span></button>
            </div>
            <div class="fa-card__info">
              <a href="${escapeAttr(item.url)}" class="fa-card__title">${escapeHtml(item.title)}</a>
              <div class="fa-card__meta">
                <span class="fa-card__price">${escapeHtml(item.price || '')}</span>
              </div>
            </div>
          </li>`
        )
        .join('');

      this.innerHTML = `<ul class="fa-product-grid" style="--fa-columns-desktop:4;--fa-columns-tablet:3;--fa-columns-mobile:2">${cards}</ul>`;
    }
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/"/g, '&quot;');
  }

  if (!customElements.get('fa-wishlist-button')) customElements.define('fa-wishlist-button', FaWishlistButton);
  if (!customElements.get('fa-wishlist-count')) customElements.define('fa-wishlist-count', FaWishlistCount);
  if (!customElements.get('fa-wishlist-list')) customElements.define('fa-wishlist-list', FaWishlistList);
})();
